import { AlertTriangle, Ban, ShieldOff, FileWarning, Scale, Smartphone } from "lucide-react";
import { useEffect } from "react";
import { Section } from "@/components/site/PageShell";
import StoreBadges from "@/components/site/StoreBadges";
import { useAppModal } from "@/context/AppModalContext";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";
import { trackEvent } from "@/lib/analytics";

/* , THE PLEDGE ITSELF , */
/* DRAFT, PENDING LEGAL REVIEW */
const PLEDGE_CLAUSES = [
  "I will use YeboNadi to document my own interactions and environment, lawfully.",
  "I will not use any recording to blackmail, extort, harass, coerce, intimidate, or unlawfully surveil any person.",
  "I will comply with the recording-consent laws that apply where I record.",
  "I understand that every retrieval of a sealed recording, requiring a legal order or signed release along with my encryption/decryption key, is logged in an append-only audit trail, and that breaking this undertaking ends my access and may be reported where the law requires.",
];

function UndertakingDocument() {
  return (
    <div
      className="relative rounded-2xl border-2 border-[color:var(--yn-ink)]/10 shadow-sm"
      style={{ background: "#FDFCF7" }}
      data-testid={SITE.undertakingDocument}
    >
      <div className="px-6 sm:px-12 lg:px-16 py-14 sm:py-16 lg:py-20">
        {/* Document header */}
        <div className="text-center">
          <p className="font-heading text-[11px] font-semibold uppercase tracking-[0.3em] text-[color:var(--yn-primary)]">
            The YeboNadi
          </p>
          <p className="mt-2 font-pledge text-4xl sm:text-5xl font-semibold text-[color:var(--yn-ink)]">
            Undertaking
          </p>
          <div className="mx-auto mt-6 h-px w-16 bg-[color:var(--yn-ink)]/25" />
        </div>

        {/* Preamble */}
        <p className="mt-10 font-pledge text-lg sm:text-xl italic text-[color:var(--yn-ink)]/75 text-center max-w-2xl mx-auto leading-relaxed">
          Before making my first sealed recording, I make the following
          undertaking, freely and knowingly:
        </p>

        {/* Clauses */}
        <ol
          className="mt-12 max-w-2xl mx-auto space-y-8"
          data-testid={SITE.undertakingClauses}
        >
          {PLEDGE_CLAUSES.map((clause, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr] gap-5 sm:gap-6 items-baseline">
              <span className="font-pledge text-3xl sm:text-4xl font-medium text-[color:var(--yn-primary)] leading-none">
                {romanNumeral(i + 1)}.
              </span>
              <p className="font-pledge text-lg sm:text-[21px] leading-[1.55] text-[color:var(--yn-ink)]">
                {clause}
              </p>
            </li>
          ))}
        </ol>

        {/* Signature block */}
        <div className="mt-16 max-w-2xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-8 sm:gap-12 items-end">
            <div>
              <div className="h-px w-full bg-[color:var(--yn-ink)]/40" />
              <p className="mt-2 font-pledge text-sm italic text-[color:var(--yn-ink)]/60">
                Signed by you, inside the app, before your first recording.
              </p>
            </div>
            <div>
              <div className="h-px w-40 bg-[color:var(--yn-ink)]/40" />
              <p className="mt-2 font-pledge text-sm italic text-[color:var(--yn-ink)]/60">
                Date of installation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* , WHY WE ASK , */
const WHY_PARAS = [
  "Evidence tools attract two very different kinds of people. The vulnerable, who need a way to hold on to what actually happened when no one is listening. And the malicious, who would use the same tool as a weapon against someone else.",
  "The Undertaking is the first of three things that keep these two groups apart. The other two are automatic: sealed recordings can't be played back inside the app, and every retrieval, always requiring a legal order or signed release, plus your encryption/decryption key, is written to an append-only log. Together, they make YeboNadi a very bad tool for the second group, and a genuinely useful one for the first.",
  "The Undertaking sits alongside those protections, not in place of them. Together, a binding pledge, a sealed-vault architecture, and a formal release process, they make evidence useful only in the ways the people who need it should use it.",
];

/* , CONSEQUENCES , */
const CONSEQUENCES = [
  {
    icon: Ban,
    title: "Immediate suspension of your account.",
    body: "Access to your vault is suspended pending review. Sealed recordings remain encrypted; nothing is silently deleted.",
  },
  {
    icon: ShieldOff,
    title: "Refusal of pending or future retrievals.",
    body: "Any pending retrieval requests are refused. New retrievals are blocked while the account is under review; sealed recordings remain encrypted and untouched.",
  },
  {
    icon: FileWarning,
    title: "Reporting where the law requires.",
    body: "Where applicable law requires it, Simple Concepts LLC will cooperate with law enforcement and, where appropriate, notify affected parties.",
  },
  {
    icon: Scale,
    title: "Civil and criminal exposure.",
    body: "Breaking this Undertaking may expose the account holder to civil claims and criminal liability under the laws of the applicable jurisdiction, independent of any action we take.",
  },
];

/* Roman numerals I, II, III, IV, small helper */
function romanNumeral(n) {
  return ["I", "II", "III", "IV", "V", "VI", "VII"][n - 1] || String(n);
}

/* , PAGE , */
export default function Undertaking() {
  useMeta({
    title: "The YeboNadi Undertaking, the promise every user signs",
    description:
      "Every YeboNadi user signs the Undertaking before their first recording. Four clauses, plainly written. The pledge that keeps evidence useful for the vulnerable and useless for the malicious.",
    path: "/undertaking",
  });
  const { openModal } = useAppModal();

  useEffect(() => {
    trackEvent("undertaking_read", { page: "/undertaking" });
  }, []);

  return (
    <div data-testid={SITE.undertakingPage}>
      {/* HERO */}
      <section className="pt-14 sm:pt-20 lg:pt-24 pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            The Undertaking
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[color:var(--yn-ink)]">
            Power like this needs a promise.
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-[color:var(--yn-muted)] max-w-2xl mx-auto">
            Every YeboNadi user signs the Undertaking before their first
            recording.
          </p>
        </div>
      </section>

      {/* Legal-defer callout, the authoritative Undertaking lives in the app */}
      <section className="pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl border border-[color:var(--yn-primary)]/30 bg-[color:var(--yn-primary)]/5 p-6 sm:p-8"
            data-testid="site-undertaking-defer-callout"
          >
            <div className="flex items-start gap-3">
              <Smartphone
                size={22}
                className="mt-1 shrink-0"
                style={{ color: "var(--yn-primary)" }}
              />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
                  Authoritative version, inside the app
                </p>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-ink)]">
                  The binding Undertaking lives inside the YeboNadi app, it
                  is what you read and tap to sign before your first
                  recording. This page is a plain-English reference summary
                  for the public web. If anything here differs from the
                  in-app pledge, the in-app pledge is what applies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE UNDERTAKING DOCUMENT */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <UndertakingDocument />
        </div>
      </section>

      {/* WHY WE ASK */}
      <Section
        className="border-y border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)]"
        data-testid={SITE.undertakingWhyWeAsk}
      >
        <div className="max-w-3xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Why we ask
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
            Because the alternative is worse for you.
          </h2>
          <div className="mt-8 space-y-6 text-[17px] leading-[1.75] text-[color:var(--yn-muted)]">
            {WHY_PARAS.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </Section>

      {/* CONSEQUENCES */}
      <Section data-testid={SITE.undertakingConsequences}>
        <div className="max-w-3xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            If it&apos;s broken
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
            What happens.
          </h2>

          <ol className="mt-10 divide-y divide-[color:var(--yn-border)]">
            {CONSEQUENCES.map((c, i) => (
              <li key={c.title} className="py-6 first:pt-0 last:pb-0 grid grid-cols-[auto_1fr] gap-5 items-start">
                <span
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{ background: "rgba(26,108,196,0.08)" }}
                >
                  <c.icon size={20} style={{ color: "var(--yn-primary)" }} />
                </span>
                <div>
                  <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                    <span className="text-[color:var(--yn-primary)] mr-2">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {c.title}
                  </p>
                  <p className="mt-2 text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
                    {c.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      {/* CTA */}
      <section
        className="py-20 md:py-28 border-t border-[color:var(--yn-border)]"
        data-testid={SITE.undertakingCta}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1] text-[color:var(--yn-ink)] max-w-3xl mx-auto">
            The Undertaking is the first tap after install.
          </h2>
          <p className="mt-5 text-lg text-[color:var(--yn-muted)] max-w-xl mx-auto">
            Get the app. Read the Undertaking on your own phone. Sign it or
            don&apos;t, either is a real answer.
          </p>

          <div className="mt-10 flex flex-col items-center gap-5">
            <StoreBadges
              placement="undertaking-cta"
              appTestId={SITE.finalCtaAppStoreBadge}
              playTestId={SITE.finalCtaPlayStoreBadge}
            />
            <button
              type="button"
              onClick={() => openModal("undertaking-cta")}
              className="text-sm font-medium text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
            >
              Or open the install panel →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
